import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { Pressable, StyleSheet, Text, View, Animated } from 'react-native';
import { IThemeContext, useThemeContext } from '../../contexts';
import { getResponsivePadding, getResponsiveFontSize } from '../../helpers/utils';

type FooterButtonProps = {
  title: string;
  onPress: () => void;
  isActive: boolean;
  icon: React.ReactNode;
  badgeCount?: number;
  jiggle?: boolean;
};

/**
 * FooterButton component with responsive design
 *
 * @param {PropsWithChildren<FooterButtonProps>} props
 * @return {React.FC}
 */
const FooterButton: React.FC<PropsWithChildren<FooterButtonProps>> = (props) => {
  const { theme } = useThemeContext();
  const jiggleAnimation = useRef(new Animated.Value(0)).current;

  const styles = createFooterButtonStyles(theme);

  useEffect(() => {
    if (props.jiggle) {
      const jiggle = () => {
        Animated.sequence([
          Animated.timing(jiggleAnimation, {
            toValue: 1,
            duration: 50,
            useNativeDriver: false
          }),
          Animated.timing(jiggleAnimation, {
            toValue: -1,
            duration: 50,
            useNativeDriver: false
          }),
          Animated.timing(jiggleAnimation, {
            toValue: 0,
            duration: 50,
            useNativeDriver: false
          })
        ]).start(() => {
          setTimeout(jiggle, 2000);
        });
      };

      jiggle();

      return () => {
        jiggleAnimation.stopAnimation();
      };
    }
  }, [props.jiggle, jiggleAnimation]);

  const jiggleStyle = props.jiggle
    ? {
        transform: [
          {
            rotate: jiggleAnimation.interpolate({
              inputRange: [-1, 1],
              outputRange: ['-15deg', '15deg']
            })
          }
        ]
      }
    : {};

  return (
    <Pressable style={styles.footerButtonStyle} onPress={props.onPress}>
      <View>
        <Animated.View style={jiggleStyle}>{props.icon}</Animated.View>
        {props.badgeCount !== undefined && props.badgeCount > 0 && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{props.badgeCount}</Text>
          </View>
        )}
      </View>
      <Text style={[styles.footerButtonTextStyle, props.isActive && styles.footerButtonActive]}>
        {props.title}
      </Text>
    </Pressable>
  );
};

export default FooterButton;

const createFooterButtonStyles = (theme: IThemeContext['theme']) => {
  const styles = StyleSheet.create({
    footerButtonStyle: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: getResponsivePadding(6, 7, 8),
      flex: 1,
      padding: getResponsivePadding(12, 14, 16)
    },
    footerButtonActive: {
      fontWeight: 'bold'
    },
    footerButtonTextStyle: {
      color: theme.primary,
      fontSize: getResponsiveFontSize(10, 11, 12),
      textAlign: 'center'
    },
    footerButtonIconStyle: {
      width: 24,
      height: 24,
      color: theme.primary
    },
    badge: {
      position: 'absolute',
      bottom: 15,
      left: 15,
      backgroundColor: 'red',
      borderRadius: 100,
      width: 21,
      height: 21,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 4
    },
    badgeText: {
      color: '#fff',
      fontSize: getResponsiveFontSize(12, 13, 15),
      fontWeight: 'bold'
    }
  });

  return styles;
};
